import React, { useState, useEffect } from 'react';
import axios from '../../../config/axios';
import { Button } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useApi } from '../../lib/api';
import ProfileShell, { ProfileLabel, ProfileSection, profileInputStyle } from './ProfileShell';

/**
 * ProfileGarden - lokalizacja, strefa klimatyczna, daty przymrozków.
 *
 * Endpointy:
 *   GET /api/auth/profile - aktualny stan
 *   PUT /api/auth/profile - zapis (city, latitude, longitude, hardiness_zone,
 *                                  first_frost_date, last_frost_date)
 *   GET /api/weather/location - geocoding miasta przez Nominatim (jeśli backend
 *                               wspiera) lub ustawienie współrzędnych
 *   PUT /api/weather/location - zapis lokalizacji + auto-detect strefy
 */

const ZONES = ['5a', '5b', '6a', '6b', '7a', '7b', '8a'];

// ── CityAutocomplete (Nominatim OSM) ──────────────────────────────
function CityAutocomplete({ value, onChange, onPick }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // Debounce 350ms — Nominatim wymaga max 1 req/sec
  useEffect(() => {
    if (!query || query.length < 3) { setResults([]); return; }
    if (query === value) return; // nie pytaj gdy user nic nie zmienił
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(query)}`;
        const res = await fetch(url, {
          headers: { 'Accept-Language': 'pl', 'User-Agent': 'GardenApp/1.0' },
        });
        const data = await res.json();
        const items = (data || []).map(d => ({
          name: d.display_name,
          short: d.address?.city || d.address?.town || d.address?.village || d.address?.municipality || d.name || d.display_name.split(',')[0],
          country: d.address?.country,
          lat: parseFloat(d.lat),
          lon: parseFloat(d.lon),
          place_id: d.place_id,
        }));
        setResults(items);
        setOpen(items.length > 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSelect = (item) => {
    onPick?.(item);
    setOpen(false);
    setResults([]);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        aria-label="Miejscowość"
        value={query}
        onChange={e => { setQuery(e.target.value); onChange?.(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="np. Warszawa"
        autoComplete="off"
        style={profileInputStyle}
      />
      {loading && (
        <div style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          color: 'var(--t-text-mute)', letterSpacing: '.18em',
        }}>SZUKAM…</div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          zIndex: 50, maxHeight: 280, overflowY: 'auto',
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line-2)',
          borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,.5)',
          padding: 4,
        }}>
          {results.map(r => (
            <button
              key={r.place_id}
              type="button"
              onMouseDown={() => handleSelect(r)}
              style={{
                width: '100%', textAlign: 'left',
                padding: '10px 12px', borderRadius: 8,
                background: 'transparent', border: 'none',
                color: 'var(--t-text)', cursor: 'pointer',
                display: 'block',
                transition: 'background 150ms ease',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--t-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                fontSize: 13, fontWeight: 500,
                color: 'var(--t-text)', letterSpacing: '-.01em',
              }}>{r.short}{r.country ? `, ${r.country}` : ''}</div>
              <div style={{
                fontSize: 11, color: 'var(--t-text-dim)',
                marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{r.name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileGarden() {
  const { data: profile, loading, error, refetch } = useApi('/api/auth/profile');

  const [form, setForm] = useState({
    city: '',
    latitude: '',
    longitude: '',
    hardiness_zone: '',
    first_frost_date: '',
    last_frost_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState('');

  // Inicjalizacja formularza po załadowaniu danych
  useEffect(() => {
    if (profile) {
      setForm({
        city: profile.city || '',
        latitude: profile.latitude || '',
        longitude: profile.longitude || '',
        hardiness_zone: profile.hardiness_zone || '',
        first_frost_date: profile.first_frost_date || '',
        last_frost_date: profile.last_frost_date || '',
      });
    }
  }, [profile]);

  if (error?.status === 401) {
    return <ProfileShell><ErrorState error={error} /></ProfileShell>;
  }
  if (loading) return <ProfileShell><LoadingState /></ProfileShell>;

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setSaveError('Twoja przeglądarka nie wspiera geolokalizacji.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm({
          ...form,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        });
      },
      () => setSaveError('Nie udało się pobrać lokalizacji.')
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await axios.put('/api/auth/profile', {
        city: form.city || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        hardiness_zone: form.hardiness_zone || null,
        first_frost_date: form.first_frost_date || null,
        last_frost_date: form.last_frost_date || null,
      });
      setSavedAt(new Date());
      refetch();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = profile && (
    form.city !== (profile.city || '') ||
    String(form.latitude) !== String(profile.latitude || '') ||
    String(form.longitude) !== String(profile.longitude || '') ||
    form.hardiness_zone !== (profile.hardiness_zone || '') ||
    form.first_frost_date !== (profile.first_frost_date || '') ||
    form.last_frost_date !== (profile.last_frost_date || '')
  );

  return (
    <ProfileShell
      eyebrow="Mój ogród"
      title="Lokalizacja i sezon"
      subtitle="Te ustawienia decydują o datach siewu, alertach przymrozków i prognozie pogody. Im dokładniej, tym lepsze sugestie."
    >
      {saveError && (
        <div style={{
          padding: '12px 14px', borderRadius: 10,
          background: 'rgba(192, 106, 74, 0.08)',
          borderLeft: '3px solid var(--t-rust)',
          fontSize: 13, color: 'var(--t-text)', marginBottom: 22,
        }}>{saveError}</div>
      )}

      <ProfileSection
        title="Lokalizacja"
        description="Miasto + współrzędne. Możesz wpisać ręcznie albo użyć geolokalizacji przeglądarki."
      >
        <div style={{ marginBottom: 16 }}>
          <ProfileLabel htmlFor="city">Miasto</ProfileLabel>
          <CityAutocomplete
            value={form.city}
            onChange={(v) => setField('city', v)}
            onPick={(place) => {
              setField('city', place.short || place.name);
              if (place.lat) setField('latitude', String(place.lat));
              if (place.lon) setField('longitude', String(place.lon));
            }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <ProfileLabel htmlFor="latitude">Szerokość</ProfileLabel>
            <input
              id="latitude"
              type="text"
              value={form.latitude}
              onChange={e => setField('latitude', e.target.value)}
              placeholder="52.2297"
              style={profileInputStyle}
              inputMode="decimal"
            />
          </div>
          <div>
            <ProfileLabel htmlFor="longitude">Długość</ProfileLabel>
            <input
              id="longitude"
              type="text"
              value={form.longitude}
              onChange={e => setField('longitude', e.target.value)}
              placeholder="21.0122"
              style={profileInputStyle}
              inputMode="decimal"
            />
          </div>
        </div>
        <Button variant="ghost" onClick={handleGeolocation}>
          📍 Użyj mojej lokalizacji
        </Button>
      </ProfileSection>

      <ProfileSection
        title="Strefa mrozoodporności"
        description="Polska to strefy 5-8. Decyduje, które rośliny przetrwają zimę bez okrywy."
      >
        <ProfileLabel htmlFor="zone">Strefa</ProfileLabel>
        <select
          id="zone"
          value={form.hardiness_zone}
          onChange={e => setField('hardiness_zone', e.target.value)}
          style={{ ...profileInputStyle, colorScheme: 'dark' }}
        >
          <option value="">— wybierz —</option>
          {ZONES.map(z => <option key={z} value={z}>Strefa {z}</option>)}
        </select>
      </ProfileSection>

      <ProfileSection
        title="Daty przymrozków"
        description="Ostatni przymrozek wiosną decyduje, kiedy można sadzić wrażliwe rośliny do gruntu (pomidor, papryka, ogórek). Pierwszy jesienią — kiedy okryć."
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <ProfileLabel htmlFor="last_frost">Ostatni wiosną</ProfileLabel>
            <input
              id="last_frost"
              type="date"
              value={form.last_frost_date}
              onChange={e => setField('last_frost_date', e.target.value)}
              style={{ ...profileInputStyle, colorScheme: 'dark' }}
            />
          </div>
          <div>
            <ProfileLabel htmlFor="first_frost">Pierwszy jesienią</ProfileLabel>
            <input
              id="first_frost"
              type="date"
              value={form.first_frost_date}
              onChange={e => setField('first_frost_date', e.target.value)}
              style={{ ...profileInputStyle, colorScheme: 'dark' }}
            />
          </div>
        </div>
      </ProfileSection>

      <div style={{
        position: 'sticky', bottom: 16,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 14, padding: '14px 0',
      }}>
        {savedAt && !isDirty && (
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
            color: 'var(--t-sage)',
          }}>✓ Zapisano</div>
        )}
        <div style={{ flex: 1 }} />
        <Button
          onClick={handleSave}
          variant={isDirty && !saving ? 'primary' : 'ghost'}
        >{saving ? 'Zapisuję…' : 'Zapisz zmiany'}</Button>
      </div>
    </ProfileShell>
  );
}
